/**
 * Decodes an OUTBOUND UiCmd frame back off the wire.
 *
 * `uiCmdBuilder.createUiCmdPayload` builds the bytes; this module reads those
 * same bytes back with the generated root accessor and `unpack()`, then shapes
 * the result as a Terminal table row. Decoding the payload rather than
 * reformatting the request object is the whole point: what the Terminal shows
 * is what left the browser, so a builder that drops or mis-encodes a field
 * shows up as a wrong row instead of a right-looking one.
 */

import * as flatbuffers from "flatbuffers";

// ROOT
import { UiCmd, UiCmdT } from "../generated/ui-cmd/ui-cmd";

// CONTROL MESSAGE
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { NormalControlT } from "../generated/control/normal-control";
import { ValuedControlT } from "../generated/control/valued-control";
import { Control } from "../generated/control/control";

// ENVELOPE ENUMS
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";

// INTERFACES
import {
  NO_ACK_VERDICT,
  TerminalRowInterface,
  TerminalTxMeta,
} from "../../interfaces/Monitoring.interface";
import PositionCallsStatusT from "../../types/PositionCallsStatus";
import { EMPTY_UUID } from "../../utils/uuid";

/** Raw frame as published by `uiCmdTxBus`, including its send-ordering number. */
export interface UiCmdTxFrame {
  payload: Uint8Array;
  seq: number;
  serverId: string;
  serverName: string;
  channelId: string;
  sentAt: number;
}

/**
 * Read the UiCmd back out of the sent bytes.
 *
 * Returns null when the buffer is not a readable UiCmd — a truncated or
 * corrupt frame should render nothing rather than a half-populated row that
 * an operator would read as fact.
 *
 * The required-field check is load-bearing, not belt-and-braces:
 * `getRootAsUiCmd` does NOT throw on arbitrary bytes. It happily reads a
 * vtable out of whatever is there and yields a table whose every field is
 * absent, so `[9, 9, 9]` unpacks to a perfectly well-formed-looking UiCmdT
 * with `msg: null`. `msg`, `channel_id` and `bank_id` are required in the
 * schema (their `pack()` throws when unset), so any frame this browser
 * actually produced has all three — their absence means the bytes were never
 * a UiCmd.
 */
export function decodeUiCmdPayload(payload: Uint8Array): UiCmdT | null {
  // A FlatBuffer root is preceded by a 4-byte offset; anything shorter cannot
  // be read at all and would index past the end of the buffer.
  if (payload.length < 4) return null;
  try {
    // Copy into a fresh buffer: `payload` is the array handed to send(), and
    // ByteBuffer keeps a live reference to whatever it is given.
    const buffer = new flatbuffers.ByteBuffer(new Uint8Array(payload));
    const uiCmd = UiCmd.getRootAsUiCmd(buffer).unpack();
    if (!uiCmd.msg || !uiCmd.channelId || !uiCmd.bankId) return null;
    return uiCmd;
  } catch {
    return null;
  }
}

/**
 * The device-specific control body inside a UiCmd, whichever variant it is.
 * Elevator messages carry a floor `position`; escalator messages do not.
 */
function controlBody(uiCmd: UiCmdT): ElevatorControlMessageT | EscalatorControlMessageT | null {
  const msg = uiCmd.msg;
  if (!msg) return null;
  if (msg.controlType === DeviceControlMessage.ElevatorControlMessage) {
    return msg.control as ElevatorControlMessageT;
  }
  if (msg.controlType === DeviceControlMessage.EscalatorControlMessage) {
    return msg.control as EscalatorControlMessageT;
  }
  return null;
}

/**
 * The Control enum leaf, reached through whichever control variant the frame
 * used. Both NormalControl and ValuedControl expose the leaf as `message`.
 */
export function controlLeaf(uiCmd: UiCmdT): Control | null {
  const body = controlBody(uiCmd);
  const control = body?.control as NormalControlT | ValuedControlT | null | undefined;
  return control ? control.message : null;
}

/** Floor index carried by an elevator control message; null for escalators. */
export function controlPosition(uiCmd: UiCmdT): number | null {
  const body = controlBody(uiCmd);
  return body instanceof ElevatorControlMessageT ? body.position : null;
}

/** "EnergizeCarCall" for a known variant, the raw number for an unknown one. */
export function controlDisplayName(leaf: Control | null): string {
  if (leaf === null) return "--";
  const name = (Control as unknown as Record<number, string>)[leaf];
  return name ?? String(leaf);
}

/** "1c 00 00 00 …" — one lowercase byte pair per byte, in wire order. */
export function toHex(payload: Uint8Array): string {
  return Array.from(payload)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join(" ");
}

/**
 * Turn a sent frame into a Terminal table row.
 *
 * The row is a real `SocketMessageInterface` so every existing column keeps
 * working, with two deliberate choices:
 *
 * - `contentType` is `NONE`. A UiCmd is its own FlatBuffer root; it has no
 *   BitMask/OneHot envelope, and claiming one would be a lie the Message
 *   column would then dutifully print.
 * - `content` is null for the same reason. The full decoded tree lives on
 *   `txMeta.uiCmd`, where it is typed as the generated `UiCmdT` instead of
 *   being squeezed into an envelope type it does not belong to.
 *
 * Returns null when the payload does not decode.
 */
export function txFrameToTerminalRow(frame: UiCmdTxFrame): TerminalRowInterface | null {
  const uiCmd = decodeUiCmdPayload(frame.payload);
  if (!uiCmd) return null;

  const leaf = controlLeaf(uiCmd);
  const txMeta: TerminalTxMeta = {
    uiCmd,
    seq: frame.seq,
    commandName: controlDisplayName(leaf),
    hex: toHex(frame.payload),
    byteLength: frame.payload.length,
    sentAt: frame.sentAt,
    serverId: frame.serverId,
    serverName: frame.serverName,
    channelId: frame.channelId,
  };

  return {
    // Identity comes off the wire, not off the request object, so a builder
    // that mis-encodes a UUID shows a mismatched row instead of a matching one.
    bankId: uiCmd.bankId ?? EMPTY_UUID,
    liftId: uiCmd.liftId ?? EMPTY_UUID,
    carCalls: new PositionCallsStatusT(),
    hallCalls: {
      up: new PositionCallsStatusT(),
      down: new PositionCallsStatusT(),
      upAssigned: new PositionCallsStatusT(),
      downAssigned: new PositionCallsStatusT(),
    },
    faults: false,
    activeFaults: [],
    position: controlPosition(uiCmd),
    serviceMode: null,
    direction: null,
    doorStatus: {
      frontOpen: false,
      rearOpen: false,
      frontStalled: false,
      rearStalled: false,
    },
    doorZone: null,
    levelling: false,
    escalatorControl: null,
    connected: false,
    msg: {
      contentType: EncodedMessageContent.NONE,
      messageType: MessageContent.Control_ControlMessage,
      content: null,
      active: uiCmd.active,
    },
    usage: {},
    bitmask: {},
    positionMap: {},
    serviceMap: {},
    oneHotMap: {},
    // Outbound rows report a command being SENT, never an answer to one —
    // and so can never report one being withheld either.
    ack: NO_ACK_VERDICT,
    withheldCalls: [],
    txMeta,
  };
}
